import { Platform, StyleSheet, View } from "react-native";

import { COLORS } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;
import Svg, { G, Path } from "react-native-svg";

// Reusable on-brand backdrop. Renders the same palm-frond motif used on the
// marketing landing page (sage strokes on the sand portal, faintly visible)
// so the student portal and per-program workspace surfaces feel of a piece
// with coravu.com instead of plain flat sand.
//
// Sits absolute-positioned behind page content via pointerEvents="none". The
// host view should set its own background colour; this component contributes
// only the decorative SVGs.


type Tone = "sand" | "dark";

const SAND_OPACITIES = {
  topRight: 0.14,
  bottomLeft: 0.12,
  centerAmber: 0.1,
};

// Slightly higher opacity on dark surfaces — the sage strokes need to fight
// the forest backdrop to stay visible. Mirrors the marketing hero treatment.
const DARK_OPACITIES = {
  topRight: 0.12,
  bottomLeft: 0.1,
  centerAmber: 0.08,
};

export function BrandBackdrop({ tone = "sand" }: { tone?: Tone }) {
  // SVG performance on native is fine but the pattern is decorative web
  // chrome — we skip native to avoid layout shifts on small screens.
  if (Platform.OS !== "web") return null;

  const op = tone === "dark" ? DARK_OPACITIES : SAND_OPACITIES;
  const palmStroke = tone === "dark" ? COLORS.SAGE.hex : COLORS.SAGE.hex;

  return (
    <View
      pointerEvents="none"
      style={[
        StyleSheet.absoluteFillObject,
        // NO overflow:hidden — the branches need to be able to bleed
        // past the section boundary so the beige hero's bottom palm
        // and the green section's top palm visually connect across
        // the divide. Clipping was happening here (independent of the
        // parent section's overflow setting), preventing the
        // cross-boundary continuity Luke asked for.
        { zIndex: 0 },
      ]}
    >
      {/* Top-right palm — anchored off the edge so the spine sits beyond the
          viewport and only the curving fronds reach into the frame. */}
      <Svg
        width={1100}
        height={1100}
        viewBox="0 0 1100 1100"
        style={{
          position: "absolute",
          right: -120,
          top: -80,
          opacity: op.topRight,
        }}
      >
        <G stroke={palmStroke} strokeLinecap="round" fill="none">
          <Path
            d="M550 1060 C535 880 500 700 460 560 C428 440 385 340 355 240 C330 160 318 90 310 40"
            strokeWidth={4}
          />
          <Path
            d="M460 560 C560 510 680 478 790 440 C880 408 960 368 1020 328 C1070 295 1095 265 1100 240"
            strokeWidth={3}
          />
          <Path d="M475 620 C578 574 700 544 812 508 C904 478 988 438 1048 400" strokeWidth={2.5} />
          <Path
            d="M455 500 C550 452 666 420 772 384 C858 354 935 314 990 275 C1035 243 1058 214 1062 190"
            strokeWidth={2.2}
          />
          <Path d="M440 440 C530 392 640 360 742 325 C825 295 898 257 950 220" strokeWidth={2} />
          <Path d="M460 560 C365 510 248 478 142 440 C55 408 -22 368 -80 328" strokeWidth={3} />
          <Path d="M470 600 C372 554 252 524 142 488 C52 458 -30 418 -88 380" strokeWidth={2.5} />
          <Path d="M680 478 C672 448 658 422 638 394" strokeWidth={1.4} />
          <Path d="M760 448 C752 418 738 392 718 364" strokeWidth={1.4} />
          <Path d="M840 414 C832 384 818 358 798 330" strokeWidth={1.4} />
          <Path d="M600 500 C592 470 578 444 560 416" strokeWidth={1.4} />
          <Path d="M240 478 C248 448 262 422 282 394" strokeWidth={1.4} />
          <Path d="M160 448 C168 418 182 392 202 364" strokeWidth={1.4} />
          <Path d="M390 320 C440 292 510 272 575 248 C630 228 678 204 710 180" strokeWidth={2} />
          <Path d="M375 260 C422 232 488 212 550 190 C602 172 648 150 678 128" strokeWidth={1.8} />
          <Path d="M362 200 C405 174 468 156 526 136 C576 118 618 97 645 76" strokeWidth={1.6} />
        </G>
      </Svg>

      {/* Bottom-left palm — rotated + flipped so it grows up-and-right from
          the lower-left corner. Same SVG family as the marketing hero. */}
      <Svg
        width={820}
        height={820}
        viewBox="0 0 820 820"
        style={{
          position: "absolute",
          left: -140,
          bottom: -120,
          opacity: op.bottomLeft,
          transform: [{ rotate: "160deg" }, { scaleX: -1 }],
        }}
      >
        <G stroke={palmStroke} strokeLinecap="round" fill="none">
          <Path
            d="M410 790 C398 650 372 510 342 400 C318 310 286 230 265 160 C248 104 240 58 235 18"
            strokeWidth={3.2}
          />
          <Path
            d="M342 400 C420 364 512 340 596 308 C666 281 726 248 768 218 C802 193 818 168 820 146"
            strokeWidth={2.4}
          />
          <Path d="M352 448 C432 414 526 392 612 362 C684 336 746 304 788 275" strokeWidth={2} />
          <Path d="M342 400 C262 364 172 340 90 308 C22 281 -36 248 -76 218" strokeWidth={2.4} />
          <Path d="M510 340 C504 314 494 292 478 268" strokeWidth={1.2} />
          <Path d="M582 310 C576 284 566 262 550 238" strokeWidth={1.2} />
          <Path d="M190 340 C196 314 206 292 222 268" strokeWidth={1.2} />
        </G>
      </Svg>

      {/* Smaller amber palm — soft warm accent, sits high-center behind the
          page chrome. Lower opacity so it reads as a glow rather than a line. */}
      <Svg
        width={600}
        height={600}
        viewBox="0 0 600 600"
        fill="none"
        style={{
          position: "absolute",
          right: 240,
          top: -160,
          opacity: op.centerAmber,
        }}
      >
        <G stroke={STUDENT_THEME.accentBorderSoft} strokeLinecap="round">
          <Path
            d="M300 580 C292 480 275 380 255 290 C238 215 215 150 200 95 C187 50 180 18 178 0"
            strokeWidth={2.8}
          />
          <Path d="M255 290 C308 266 370 248 428 222 C476 200 516 174 544 150" strokeWidth={2} />
          <Path d="M262 322 C316 300 380 284 440 260 C490 240 532 215 558 192" strokeWidth={1.6} />
          <Path d="M255 290 C202 266 142 248 86 222 C40 200 2 174 -24 150" strokeWidth={2} />
          <Path d="M356 248 C350 224 340 204 325 182" strokeWidth={1.1} />
          <Path d="M410 225 C404 201 394 181 380 159" strokeWidth={1.1} />
          <Path d="M155 248 C160 224 170 204 184 182" strokeWidth={1.1} />
        </G>
      </Svg>
    </View>
  );
}
